import { create } from 'zustand';
import type { Render } from '../api/types.ts';
import { db } from '../storage/db.ts';

/**
 * Render details for the owner's evaluation (`model`, `promptVersion`). The server writes them
 * onto each `aiResults[]` entry (read first, `resultMeta`); for older results this device also
 * keeps them per AI result id when it sees a render finish (fallback).
 */
export interface RenderMeta {
  model?: string;
  promptVersion?: string;
  provider?: string;
}

const KEY = 'renders.meta';
const LIMIT = 500;

export const useRenderMeta = create<{ byResult: Record<string, RenderMeta>; loaded: boolean }>(() => ({
  byResult: {},
  loaded: false,
}));

export async function loadRenderMeta(): Promise<void> {
  if (useRenderMeta.getState().loaded) return;
  const v = ((await db().kv.get(KEY))?.value as Record<string, RenderMeta> | undefined) ?? {};
  useRenderMeta.setState((s) => ({ byResult: { ...v, ...s.byResult }, loaded: true }));
}

export function metaOf(r: Partial<Pick<Render, 'model' | 'promptVersion' | 'provider'>>): RenderMeta | null {
  const m: RenderMeta = {
    ...(r.model ? { model: r.model } : {}),
    ...(r.promptVersion ? { promptVersion: r.promptVersion } : {}),
    ...(r.provider ? { provider: r.provider } : {}),
  };
  return Object.keys(m).length ? m : null;
}

/** Details written on the result itself (project.schema `$defs.aiResult`), or null. */
export function resultMeta(a: { model?: string; promptVersion?: string }): RenderMeta | null {
  return a.model || a.promptVersion ? metaOf(a) : null;
}

/** Remembers the details of a finished render for each of its results. */
export async function recordRenderMeta(r: Render): Promise<void> {
  const m = metaOf(r);
  if (!m || !r.results?.length) return;
  await loadRenderMeta();
  const next = { ...useRenderMeta.getState().byResult };
  for (const x of r.results) next[x.aiResultId] = m;
  const keys = Object.keys(next);
  for (const k of keys.slice(0, Math.max(0, keys.length - LIMIT))) delete next[k];
  useRenderMeta.setState({ byResult: next });
  await db().kv.put({ key: KEY, value: next });
}

/** "gpt-image-1 · pv-3f2a91c" (empty when unknown). */
export function renderDetails(m: RenderMeta | null | undefined): string {
  if (!m) return '';
  return [m.model, m.promptVersion].filter(Boolean).join(' · ');
}
